
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterOptions, SoccerCompetition, SportDefaultValues } from '@/types';
import { sportDefaultValues } from '@/api/gamesService';
import { getCompetitionUrlSlug, mapUrlParamToCompetition } from '@/utils/sportUtils';

export const useUrlFilters = (initialFilters: FilterOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterOptions>(initialFilters);

  // Re-initialize when initialFilters change (e.g. when navigating to a different sport page)
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters.sportType]);

  // Initialize filters from URL parameters on mount or when searchParams change
  useEffect(() => {
    const maxValueFromUrl = searchParams.get('max');
    const minValueFromUrl = searchParams.get('min');
    const competitionsFromUrl = searchParams.get('competitions');

    const newFilters = { ...initialFilters };
    
    if (maxValueFromUrl && !isNaN(Number(maxValueFromUrl))) {
      newFilters.maxValue = Number(maxValueFromUrl);
    }

    if (minValueFromUrl && !isNaN(Number(minValueFromUrl))) {
      newFilters.minValue = Number(minValueFromUrl);
    }

    // Parse competitions from comma-separated string
    if (competitionsFromUrl && initialFilters.sportType === 'soccer') {
      const urlComps = competitionsFromUrl
        .split(',')
        .map(mapUrlParamToCompetition)
        .filter((competition): competition is SoccerCompetition => Boolean(competition) && competition !== 'All');
      const validComps = Array.from(new Set(urlComps));
      if (validComps.length > 0) {
        newFilters.competitions = validComps;
      }
    }

    setFilters(newFilters);
  }, [searchParams, initialFilters]);

  // Update URL parameters when filters change
  const updateFilters = (newFilters: Partial<FilterOptions>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);

    // Build new URL parameters
    const newParams = new URLSearchParams();
    
    // Only add maxValue if it's not the default for the current sport
    const defaultMax = (sportDefaultValues[updatedFilters.sportType] as SportDefaultValues).default;
    if (updatedFilters.maxValue !== defaultMax) {
      newParams.set('max', updatedFilters.maxValue.toString());
    }
    
    // Only add minValue if it's defined and not the default
    if (updatedFilters.minValue !== undefined) {
      newParams.set('min', updatedFilters.minValue.toString());
    }
    
    // Add competitions as comma-separated list
    const competitions = (updatedFilters.competitions || []).filter(
      (competition): competition is SoccerCompetition => Boolean(competition) && competition !== 'All'
    );

    if (competitions.length > 0) {
      const compParam = competitions
        .map(encodeCompetitionForUrl)
        .join(',');
      newParams.set('competitions', compParam);
    }
    
    // Update URL without page reload
    setSearchParams(newParams, { replace: true });
  };

  // Helper function to encode competition names for URL
  const encodeCompetitionForUrl = (comp: SoccerCompetition): string => {
    return getCompetitionUrlSlug(comp);
  };

  return {
    filters,
    updateFilters
  };
};
